import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Mic, Square, Plus, X, Trash2, AlertCircle, Loader2, Clock, CheckCircle2,
  Archive, Play, Pause, Eye, Paperclip, Image as ImageIcon, FileText,
  Download, ListChecks, GripVertical, Settings2, Palette, Check, Pencil,
} from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import { toast } from "sonner";
import { openTicketAttachment, downloadTicketAttachment } from "../lib/ticketFiles";
import AppLayout, { PageHeader } from "../components/AppLayout";
import AiInsight from "../components/AiInsight";
import DossierDiscussion from "../components/DossierDiscussion";
import { api, API } from "../lib/api";
import { useLive } from "../lib/live";
import { useAuth } from "../lib/auth";
import { DossierTagsEditor, TagList, useTagFamilies } from "../components/DossierTags";
import TrelloTicketModal from "../components/trello-ticket/TrelloTicketModal";
import { Avatar } from "../components/trello-ticket/TicketMembers";
import { DossierLinker, DossierPill } from "../components/trello-ticket/DossierLinker";
import { ArchivedTicketsModal } from "../components/trello-ticket/ArchivedTickets";
import TrelloCard, { LABELS_KEY } from "../components/trello-ticket/TrelloCard";
import { TrelloColumnHeader, QuickAddCard } from "../components/trello-ticket/TrelloColumn";
import { DueBadge, DueEditor, DueFields, buildDueIso, matchesDueFilter, saveDue } from "../components/TicketDue";

function useUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/dossiers/_meta/mention-candidates").then((r) => setUsers(r.data.items || [])).catch(() => {}); }, []);
  return users;
}

function AssigneeSelect({ value, onChange, users }) {
  return (
    <div>
      <label className="overline block mb-1.5">Assigné à</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-field text-sm" data-testid="ticket-assignee-select">
        <option value="">— Personne —</option>
        {users.map((u) => <option key={u.email} value={u.email}>{u.name} ({u.email})</option>)}
      </select>
    </div>
  );
}

const DUE_FILTERS = [
  { v: "", l: "Toutes échéances" }, { v: "overdue", l: "En retard" }, { v: "today", l: "Aujourd'hui" },
  { v: "week", l: "Cette semaine" }, { v: "none", l: "Sans échéance" },
];

function TicketFilters({ tag, setTag, due, setDue, testidPrefix }) {
  const fams = useTagFamilies();
  return (
    <div className="card-flat p-3 mb-4 flex flex-wrap items-center gap-3" data-testid={`${testidPrefix}-filters`}>
      <select value={tag} onChange={(e) => setTag(e.target.value)} className="input-field max-w-[200px] text-sm" data-testid={`${testidPrefix}-filter-tag`}>
        <option value="">Tous tags</option>
        {fams.map((f) => (
          <optgroup key={f.id} label={f.name}>
            {f.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </optgroup>
        ))}
      </select>
      <select value={due} onChange={(e) => setDue(e.target.value)} className="input-field max-w-[200px] text-sm" data-testid={`${testidPrefix}-filter-due`}>
        {DUE_FILTERS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      {(tag || due) && (
        <button onClick={() => { setTag(""); setDue(""); }} className="text-xs text-norea-textMuted hover:text-norea-text underline" data-testid={`${testidPrefix}-filter-reset`}>Réinitialiser</button>
      )}
    </div>
  );
}

// Mapping {color_token from backend} -> {tailwind classes border + bg + text + headerBg}
const COLOR_PRESETS = {
  amber:  { border: "border-status-incompletBorder",  bg: "bg-status-incompletBg",  swatch: "bg-amber-400" },
  blue:   { border: "border-status-verifierBorder",   bg: "bg-status-verifierBg",   swatch: "bg-blue-400" },
  green:  { border: "border-status-completBorder",    bg: "bg-status-completBg",    swatch: "bg-emerald-400" },
  gray:   { border: "border-norea-borderStrong",      bg: "bg-norea-surfaceHover",  swatch: "bg-gray-300" },
  slate:  { border: "border-norea-border",            bg: "bg-norea-bg",            swatch: "bg-slate-300" },
  red:    { border: "border-status-erreurBorder",     bg: "bg-status-erreurBg",     swatch: "bg-red-400" },
  violet: { border: "border-violet-300",              bg: "bg-violet-50",           swatch: "bg-violet-400" },
  rose:   { border: "border-rose-300",                bg: "bg-rose-50",             swatch: "bg-rose-400" },
  teal:   { border: "border-teal-300",                bg: "bg-teal-50",             swatch: "bg-teal-400" },
};

const COLOR_OPTIONS = ["amber", "blue", "green", "gray", "slate", "red", "violet", "rose", "teal"];

function colClasses(c) {
  return COLOR_PRESETS[c?.color] || COLOR_PRESETS.slate;
}

const PRIORITY_BADGE = {
  low: { label: "Basse", cls: "bg-norea-surfaceHover text-norea-textMuted" },
  normal: { label: "Normal", cls: "bg-norea-bg text-norea-text border border-norea-border" },
  high: { label: "Haute", cls: "bg-status-erreurBg text-status-erreurText border border-status-erreurBorder" },
};

const MAX_ATTACHMENTS = 8;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Default export keeps backwards-compat: serves the assistance scope
export default function TicketsPage() {
  return (
    <TicketsKanban
      basePath="/tickets"
      title="Tickets d'assistance"
      subtitle="Demandes d'évolutions, retours, bugs — partagés entre les agents."
      accent="primary"
      testidPrefix="tickets"
    />
  );
}

export function TicketsKanban({ basePath, title, subtitle, accent = "primary", testidPrefix = "tickets" }) {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [archivesOpen, setArchivesOpen] = useState(false);
  const [tagFilter, setTagFilter] = useState("");
  const users = useUsers();
  const trello = basePath === "/internal-tickets";
  const [labelsOpen, setLabelsOpen] = useState(() => localStorage.getItem(LABELS_KEY) === "1");
  const toggleLabels = () => setLabelsOpen((v) => { localStorage.setItem(LABELS_KEY, v ? "0" : "1"); return !v; });
  const [dueFilter, setDueFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(basePath);
      setItems(data.items || []);
      setCounts(data.counts || {});
      setColumns(data.columns || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [basePath]);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("ticket");
    const t = id && items.find((x) => x.id === id);
    if (t) {
      setEditingTicket(t);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [items]);

  const { subscribe } = useLive();
  useEffect(
    () =>
      subscribe((ev) => {
        const evtPrefix = basePath.includes("internal") ? "internal_ticket" : "ticket";
        if ([`${evtPrefix}:created`, `${evtPrefix}:status`, `${evtPrefix}:deleted`, `${evtPrefix}:updated`].includes(ev.type)) load();
      }),
    [subscribe, basePath]
  );

  const moveTicket = async (id, newStatus) => {
    const prevStatus = items.find((t) => t.id === id)?.status;
    if (!prevStatus || prevStatus === newStatus) return;
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, status: newStatus } : t)));
    try {
      await api.patch(`${basePath}/${id}/status`, { status: newStatus });
      const targetCol = columns.find((c) => c.id === newStatus);
      toast.success(`Déplacé vers "${targetCol?.label || newStatus}"`, { duration: 1500 });
    } catch (e) {
      toast.error("Impossible de changer le statut");
      load();
    }
  };

  const deleteTicket = async (id) => {
    if (!window.confirm("Supprimer définitivement ce ticket ?")) return;
    try {
      await api.delete(`${basePath}/${id}`);
      toast.success("Ticket supprimé");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  // dnd-kit setup — same activation distance as DossiersPage Kanban
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [activeDragId, setActiveDragId] = useState(null);
  const activeTicket = activeDragId ? items.find((t) => t.id === activeDragId) : null;

  const grouped = useMemo(() => {
    const g = Object.fromEntries(columns.map((c) => [c.id, []]));
    items.filter((t) => (!tagFilter || (t.tags || []).includes(tagFilter)) && matchesDueFilter(t, dueFilter)).forEach((t) => {
      if (g[t.status]) g[t.status].push(t);
      else if (columns[0]) g[columns[0].id]?.push(t);
    });
    return g;
  }, [items, columns, tagFilter, dueFilter]);

  const handleDragEnd = (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || !active) return;
    const targetStatus = over.id;
    if (!columns.find((c) => c.id === targetStatus)) return;
    moveTicket(active.id, targetStatus);
  };

  return (
    <AppLayout>
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumb={[{ label: "Norea", to: "/" }, title]}
        actions={
          <div className="flex items-center gap-2">
            {trello && me?.role === "admin" && (
              <button onClick={() => setArchivesOpen(true)} className="btn-secondary" data-testid={`btn-${testidPrefix}-archives`}>
                <Archive className="w-4 h-4" />
                Archives
              </button>
            )}
            <button onClick={() => setColumnsModalOpen(true)} className="btn-secondary" data-testid={`btn-${testidPrefix}-edit-columns`}>
              <Settings2 className="w-4 h-4" />
              Colonnes
            </button>
            <button onClick={() => setOpenCreate(true)} className="btn-primary" data-testid={`btn-new-${testidPrefix === "tickets" ? "ticket" : testidPrefix}`}>
              <Plus className="w-4 h-4" />
              Nouveau ticket
            </button>
          </div>
        }
      />
      {basePath === "/internal-tickets" && <AiInsight scope="tickets" title="Synthèse IA des tickets" className="mb-5" />}
      <TicketFilters tag={tagFilter} setTag={setTagFilter} due={dueFilter} setDue={setDueFilter} testidPrefix={testidPrefix} />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={(e) => setActiveDragId(e.active.id)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDragId(null)}
      >
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid={`${testidPrefix}-kanban`}>
          {columns.map((col) => (
            <TicketColumn
              key={col.id}
              col={col}
              tickets={grouped[col.id] || []}
              loading={loading}
              count={tagFilter || dueFilter ? grouped[col.id]?.length || 0 : counts[col.id] ?? (grouped[col.id]?.length || 0)}
              me={me}
              basePath={basePath}
              onEdit={setEditingTicket}
              onDelete={deleteTicket}
              onChanged={load}
              trello={trello}
              labelsOpen={labelsOpen}
              onToggleLabels={toggleLabels}
              onManage={() => setColumnsModalOpen(true)}
            />
          ))}
        </div>
        <DragOverlay>
          {activeTicket && trello ? (
            <div className="w-[272px]"><TrelloCard ticket={activeTicket} basePath={basePath} labelsOpen={labelsOpen} dragging /></div>
          ) : activeTicket ? (
            <TicketCard ticket={activeTicket} currentEmail={me?.email} isAdmin={me?.role === "admin"} basePath={basePath} onDelete={() => {}} onEdit={() => {}} dragging />
          ) : null}
        </DragOverlay>
      </DndContext>

      {openCreate && (
        <TicketModal
          mode="create"
          basePath={basePath}
          onClose={() => setOpenCreate(false)}
          onDone={() => { setOpenCreate(false); load(); }}
        />
      )}
      {editingTicket && basePath === "/internal-tickets" && (
        <TrelloTicketModal ticketId={editingTicket.id} basePath={basePath} columns={columns} users={users} onClose={() => { setEditingTicket(null); load(); }} />
      )}
      {editingTicket && basePath !== "/internal-tickets" && (
        <TicketModal
          mode="edit"
          ticket={editingTicket}
          basePath={basePath}
          onClose={() => setEditingTicket(null)}
          onDone={() => { setEditingTicket(null); load(); }}
        />
      )}
      {archivesOpen && <ArchivedTicketsModal basePath={basePath} onClose={() => setArchivesOpen(false)} onRestored={load} />}
      {columnsModalOpen && (
        <ColumnsManager
          basePath={basePath}
          columns={columns}
          counts={counts}
          onClose={() => setColumnsModalOpen(false)}
          onSaved={() => { setColumnsModalOpen(false); load(); }}
        />
      )}
    </AppLayout>
  );
}

function TicketColumn({ col, tickets, loading, count, me, basePath, onEdit, onDelete, onChanged, trello, labelsOpen, onToggleLabels, onManage }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  const [quick, setQuick] = useState(false);
  const cls = colClasses(col);
  if (trello) {
    return (
      <div className={`w-[288px] flex-shrink-0 self-start max-h-[calc(100vh-14rem)] flex flex-col rounded-xl bg-[#ebeeec] border border-norea-border/60 ${isOver ? "ring-2 ring-norea-primary" : ""}`} data-testid={`column-${col.id}`}>
        <TrelloColumnHeader col={col} count={count} onAdd={() => setQuick(true)} onManage={onManage} />
        <div ref={setNodeRef} className="flex-1 min-h-[24px] overflow-y-auto px-2">
          {loading && <div className="h-16 rounded-lg bg-white animate-pulse mb-2" />}
          {tickets.map((t) => (
            <DraggableTicketCard key={t.id} ticket={t} trello basePath={basePath} labelsOpen={labelsOpen} onToggleLabels={onToggleLabels} onEdit={() => onEdit(t)} onChanged={onChanged} />
          ))}
        </div>
        <div className="p-2">
          <QuickAddCard col={col} basePath={basePath} open={quick} setOpen={setQuick} onCreated={onChanged} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 min-w-[260px] flex flex-col" data-testid={`column-${col.id}`}>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-t-md border-x border-t ${cls.border} ${cls.bg}`}>
        <span className={`w-2 h-2 rounded-full ${cls.swatch}`} />
        <span className="font-heading text-sm font-medium text-norea-text">{col.label}</span>
        <span className="ml-auto text-xs tabular-nums text-norea-textMuted bg-white border border-norea-border rounded-full px-2 py-0.5">
          {count}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={`flex-1 min-h-[400px] p-2 border-x border-b rounded-b-md ${cls.border} ${cls.bg} ${isOver ? "ring-2 ring-norea-primary ring-inset" : ""}`}
      >
        {loading && <div className="h-20 rounded bg-white animate-pulse" />}
        {!loading && tickets.length === 0 && (
          <div className="text-center text-xs text-norea-textMuted py-8 italic">Vide</div>
        )}
        {tickets.map((t) => (
          <DraggableTicketCard
            key={t.id}
            ticket={t}
            currentEmail={me?.email}
            isAdmin={me?.role === "admin"}
            basePath={basePath}
            onDelete={() => onDelete(t.id)}
            onEdit={() => onEdit(t)}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );
}

function DraggableTicketCard({ ticket, trello, ...rest }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: ticket.id });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={{ opacity: isDragging ? 0.3 : 1 }}>
      {trello ? <TrelloCard ticket={ticket} basePath={rest.basePath} labelsOpen={rest.labelsOpen} onToggleLabels={rest.onToggleLabels} onOpen={rest.onEdit} onChanged={rest.onChanged} /> : <TicketCard ticket={ticket} {...rest} />}
    </div>
  );
}

function TicketCard({ ticket, currentEmail, isAdmin, basePath = "/tickets", onDelete, onEdit, onChanged, dragging }) {
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);

  const canEdit = isAdmin || ticket.author_email === currentEmail;

  const playAudio = async (e) => {
    e.stopPropagation();
    if (!audioRef.current) {
      const token = localStorage.getItem("norea_token");
      const blob = await fetch(`${API}${basePath}/${ticket.id}/audio`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.blob());
      const url = URL.createObjectURL(blob);
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => setAudioPlaying(false);
    }
    if (audioPlaying) {
      audioRef.current.pause();
      setAudioPlaying(false);
    } else {
      audioRef.current.play();
      setAudioPlaying(true);
    }
  };

  const prio = PRIORITY_BADGE[ticket.priority] || PRIORITY_BADGE.normal;
  const created = new Date(ticket.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  const attachments = ticket.attachments || [];

  return (
    <div
      onClick={(e) => {
        if (e.defaultPrevented) return;
        onEdit?.();
      }}
      className={`bg-white border rounded-md p-3 mb-2 cursor-grab active:cursor-grabbing transition-shadow ${
        dragging ? "shadow-2xl rotate-1 ring-2 ring-norea-primary border-norea-primary" : "shadow-sm hover:shadow-md border-norea-border"
      } group`}
      data-testid={`ticket-${ticket.id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-norea-text leading-snug break-words">{ticket.title}</div>
        </div>
        <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded shrink-0 ${prio.cls}`}>
          {prio.label}
        </span>
      </div>
      {(ticket.tags || []).length > 0 && <div className="-mt-1 mb-2"><TagList ids={ticket.tags} /></div>}
      {(ticket.dossiers || []).length > 0 && <div className="flex flex-wrap gap-1 mb-2" onPointerDown={(e) => e.stopPropagation()}>{ticket.dossiers.map((d) => <DossierPill key={d.id} d={d} />)}</div>}
      {ticket.description && (
        <div className="text-xs text-norea-textMuted whitespace-pre-wrap line-clamp-4 mb-2">{ticket.description}</div>
      )}
      {(ticket.due_at || ticket.assignee_email || (ticket.members || []).length > 0) && (
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          <DueBadge ticket={ticket} basePath={basePath} onChange={onChanged} />
          {(ticket.members || []).map((m) => (
            <span key={m} data-testid={`ticket-${ticket.id}-member`}><Avatar email={m} size="w-5 h-5 text-[9px]" /></span>
          ))}
          {ticket.assignee_email && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-norea-primary/10 text-norea-primary font-semibold" title={`Assigné à ${ticket.assignee_email}`} data-testid={`ticket-${ticket.id}-assignee`}>
              → {ticket.assignee_email.split("@")[0]}
            </span>
          )}
        </div>
      )}
      {ticket.audio_id && (
        <button
          onClick={playAudio}
          className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-norea-accent/10 text-norea-accent hover:bg-norea-accent/20 transition mb-2"
          data-testid={`ticket-${ticket.id}-play`}
        >
          {audioPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          Écouter l'audio
        </button>
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2" data-testid={`ticket-${ticket.id}-attachments`}>
          {attachments.map((a) => (
            <AttachmentThumb key={a.id} ticketId={ticket.id} att={a} basePath={basePath} />
          ))}
        </div>
      )}
      {(ticket.checklist || []).length > 0 && (
        <div
          className="inline-flex items-center gap-1 px-1.5 py-0.5 mb-2 rounded text-[10px] font-medium border border-norea-border bg-norea-bg text-norea-text"
          data-testid={`ticket-${ticket.id}-checklist-badge`}
          title="Check-list"
        >
          <ListChecks className="w-3 h-3 text-norea-primary" />
          {ticket.checklist.filter((c) => c.done).length}/{ticket.checklist.length}
        </div>
      )}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-norea-border text-[10px] text-norea-textMuted">
        <span>{ticket.author_name}</span>
        <span>{created}</span>
      </div>
      {canEdit && (
        <div className="mt-2 flex items-center gap-3 justify-end opacity-0 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
          <button onClick={onEdit} className="text-[10px] text-norea-textMuted hover:text-norea-primary flex items-center gap-1" data-testid={`ticket-${ticket.id}-edit`}>
            <Eye className="w-3 h-3" />
            Ouvrir
          </button>
          <button onClick={onDelete} className="text-[10px] text-norea-textMuted hover:text-status-erreurText flex items-center gap-1" data-testid={`ticket-${ticket.id}-delete`}>
            <Trash2 className="w-3 h-3" />
            Supprimer
          </button>
        </div>
      )}
    </div>
  );
}

function AttachmentThumb({ ticketId, att, basePath = "/tickets" }) {
  const isImg = (att.content_type || "").startsWith("image/");
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (!isImg) return;
    let revoke = null;
    const token = localStorage.getItem("norea_token");
    fetch(`${API}${basePath}/${ticketId}/attachments/${att.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        revoke = url;
        setImgSrc(url);
      });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [ticketId, att.id, isImg, basePath]);

  const open = (e) => {
    e.stopPropagation();
    openTicketAttachment(basePath, ticketId, att);
  };

  if (isImg) {
    return (
      <button
        onClick={open}
        className="relative w-10 h-10 rounded border border-norea-border overflow-hidden hover:border-norea-primary/40 transition bg-norea-bg shrink-0"
        title={att.filename}
        data-testid={`att-thumb-${att.id}`}
      >
        {imgSrc ? <img src={imgSrc} alt={att.filename} className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 m-auto text-norea-textMuted" />}
      </button>
    );
  }
  return (
    <button
      onClick={open}
      className="inline-flex items-center gap-1 max-w-[140px] px-1.5 py-1 rounded border border-norea-border bg-norea-bg hover:border-norea-primary/40 transition text-[10px]"
      title={att.filename}
      data-testid={`att-file-${att.id}`}
    >
      <FileText className="w-3 h-3 text-norea-primary shrink-0" />
      <span className="truncate text-norea-text">{att.filename}</span>
    </button>
  );
}

// ============================================================
// Create / Edit modal — unified component
// ============================================================
function TicketModal({ mode, ticket = null, basePath = "/tickets", onClose, onDone }) {
  const isEdit = mode === "edit";
  const [tab, setTab] = useState(isEdit ? "text" : "text");
  const [title, setTitle] = useState(ticket?.title || "");
  const [description, setDescription] = useState(ticket?.description || "");
  const [priority, setPriority] = useState(ticket?.priority || "normal");
  const [assignee, setAssignee] = useState(ticket?.assignee_email || "");
  const [newDueDate, setNewDueDate] = useState("");
  const [newDueTime, setNewDueTime] = useState("");
  const [newDossiers, setNewDossiers] = useState([]);
  const users = useUsers();
  const [busy, setBusy] = useState(false);

  // Voice recording state (create only)
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Attachments
  const [stagedFiles, setStagedFiles] = useState([]); // {file, previewUrl?}
  const [existingAttachments, setExistingAttachments] = useState(ticket?.attachments || []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      stagedFiles.forEach((s) => { if (s.previewUrl) URL.revokeObjectURL(s.previewUrl); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Impossible d'accéder au micro. Autorisez le navigateur.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const resetAudio = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setSeconds(0);
  };

  const reRecord = () => {
    resetAudio();
    setTimeout(startRecording, 100);
  };

  const handleAddFiles = (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    const currentTotal = stagedFiles.length + existingAttachments.length;
    if (currentTotal + list.length > MAX_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} pièces jointes par ticket`);
      return;
    }
    const valid = [];
    for (const f of list) {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`Fichier '${f.name}' trop volumineux (max 10 Mo)`);
        continue;
      }
      valid.push({ file: f, previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null });
    }
    setStagedFiles((prev) => [...prev, ...valid]);
  };

  const removeStaged = (idx) => {
    const item = stagedFiles[idx];
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
    setStagedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeExistingAttachment = async (att) => {
    if (!window.confirm(`Supprimer la pièce jointe « ${att.filename} » ?`)) return;
    try {
      const { data } = await api.delete(`${basePath}/${ticket.id}/attachments/${att.id}`);
      setExistingAttachments(data.attachments || []);
      toast.success("Pièce jointe supprimée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const uploadStagedAttachments = async (ticketId) => {
    if (!stagedFiles.length) return;
    const form = new FormData();
    stagedFiles.forEach((s) => form.append("files", s.file));
    await api.post(`${basePath}/${ticketId}/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Titre obligatoire");
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`${basePath}/${ticket.id}`, {
          title: title.trim(),
          description,
          priority,
          assignee_email: assignee,
        });
        if (stagedFiles.length) await uploadStagedAttachments(ticket.id);
        toast.success("Ticket mis à jour");
      } else {
        let newTicket;
        if (tab === "voice" && audioBlob) {
          const form = new FormData();
          form.append("audio", audioBlob, "voice.webm");
          form.append("title", title);
          form.append("priority", priority);
          const { data } = await api.post(`${basePath}/voice`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          newTicket = data;
        } else {
          const { data } = await api.post(basePath, { title, description, priority, dossier_ids: newDossiers.map((d) => d.id) });
          newTicket = data;
        }
        if (newTicket?.id && tab === "voice" && newDossiers.length) await api.put(`${basePath}/${newTicket.id}/dossiers`, { dossier_ids: newDossiers.map((d) => d.id) });
        if (newTicket?.id && assignee) await api.patch(`${basePath}/${newTicket.id}`, { assignee_email: assignee });
        if (newTicket?.id && newDueDate) await saveDue(basePath, newTicket.id, { due_at: buildDueIso(newDueDate, newDueTime), due_has_time: !!newDueTime, due_done: false });
        if (stagedFiles.length && newTicket?.id) {
          await uploadStagedAttachments(newTicket.id);
        }
        toast.success(tab === "voice" ? "Ticket vocal créé et transcrit" : "Ticket créé");
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const fmtTime = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
  const submittable = title.trim() && (isEdit || tab === "text" || audioBlob);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={busy ? undefined : onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl border border-norea-border max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={isEdit ? "edit-ticket-modal" : "create-ticket-modal"}
      >
        <div className="px-6 py-4 border-b border-norea-border flex items-start justify-between">
          <h3 className="font-heading text-xl font-medium text-norea-text">
            {isEdit ? "Ouvrir le ticket" : "Nouveau ticket"}
          </h3>
          <button onClick={onClose} disabled={busy} className="text-norea-textMuted hover:text-norea-text">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!isEdit && (
          <div className="px-6 py-4 border-b border-norea-border">
            <div className="inline-flex items-center gap-0.5 bg-norea-bg border border-norea-border rounded-md p-0.5">
              <button
                onClick={() => setTab("text")}
                className={`px-3 py-1.5 text-xs font-medium rounded transition ${tab === "text" ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`}
                data-testid="tab-text"
              >
                Texte
              </button>
              <button
                onClick={() => setTab("voice")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition ${tab === "voice" ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`}
                data-testid="tab-voice"
              >
                <Mic className="w-3 h-3" />
                Voix
              </button>
            </div>
          </div>
        )}

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          {isEdit && (
            <DossierDiscussion dossierId={ticket.id} entityType={basePath.includes("internal") ? "internal_ticket" : "ticket"} />
          )}
          <div>
            <label className="overline block mb-1.5">Titre</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-field text-sm"
              placeholder="Résumé de la demande"
              data-testid="ticket-title-input"
            />
          </div>
          <div>
            <label className="overline block mb-1.5">Priorité</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="input-field text-sm" data-testid="ticket-priority-select">
              <option value="low">Basse</option>
              <option value="normal">Normal</option>
              <option value="high">Haute</option>
            </select>
          </div>

          {(isEdit || tab === "text") && (
            <div>
              <label className="overline block mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input-field text-sm"
                rows={6}
                placeholder="Décrivez la demande, le bug, ou l'évolution souhaitée…"
                data-testid="ticket-description-input"
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AssigneeSelect value={assignee} onChange={setAssignee} users={users} />
            {!isEdit && (
              <div>
                <label className="overline block mb-1.5">Échéance</label>
                <DueFields date={newDueDate} time={newDueTime} onDate={setNewDueDate} onTime={setNewDueTime} testid="ticket-new-due" />
              </div>
            )}
          </div>
          {!isEdit && <DossierLinker initial={newDossiers} onChange={setNewDossiers} />}
          {isEdit && (
            <>
              <DueEditor ticket={ticket} basePath={basePath} />
              <div>
                <label className="overline block mb-1.5">Tags</label>
                <DossierTagsEditor endpoint={`${basePath}/${ticket.id}/tags`} ids={ticket.tags || []} />
              </div>
              <DossierLinker basePath={basePath} ticketId={ticket.id} initial={ticket.dossiers || []} />
            </>
          )}
          {isEdit && <ChecklistEditor ticket={ticket} basePath={basePath} />}

          {!isEdit && tab === "voice" && (
            <div className="bg-norea-bg border border-norea-border rounded-md p-4 text-center">
              {!audioBlob && !recording && (
                <>
                  <Mic className="w-10 h-10 mx-auto text-norea-textMuted mb-2" />
                  <div className="text-sm text-norea-textMuted mb-3">
                    Enregistrez votre demande. Vous pourrez l'écouter avant de soumettre.
                  </div>
                  <button onClick={startRecording} className="btn-primary" data-testid="btn-record">
                    <Mic className="w-4 h-4" />
                    Démarrer l'enregistrement
                  </button>
                </>
              )}
              {recording && (
                <>
                  <div className="w-12 h-12 rounded-full bg-red-500/15 mx-auto mb-3 flex items-center justify-center animate-pulse">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                  </div>
                  <div className="text-2xl font-mono text-norea-text mb-3">{fmtTime(seconds)}</div>
                  <button onClick={stopRecording} className="btn-secondary" data-testid="btn-stop-record">
                    <Square className="w-4 h-4" />
                    Arrêter
                  </button>
                </>
              )}
              {audioBlob && !recording && (
                <>
                  <div className="text-sm text-norea-text mb-2 flex items-center gap-2 justify-center">
                    <CheckCircle2 className="w-4 h-4 text-status-completText" />
                    Enregistrement OK ({fmtTime(seconds)}) — écoutez avant d'envoyer
                  </div>
                  <audio src={audioUrl} controls className="w-full mb-3" data-testid="recorded-audio" />
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={reRecord} className="btn-secondary text-xs" data-testid="btn-rerecord">
                      <Mic className="w-3.5 h-3.5" />
                      Réenregistrer
                    </button>
                    <button onClick={resetAudio} className="btn-ghost text-xs" data-testid="btn-reset-audio">
                      <Trash2 className="w-3.5 h-3.5" />
                      Supprimer l'audio
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Attachments — common to both modes */}
          <div>
            <label className="overline block mb-1.5">
              Pièces jointes {existingAttachments.length + stagedFiles.length > 0 && (
                <span className="ml-1 text-norea-textMuted normal-case">
                  ({existingAttachments.length + stagedFiles.length}/{MAX_ATTACHMENTS})
                </span>
              )}
            </label>

            {/* Existing (edit mode) */}
            {existingAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="existing-attachments">
                {existingAttachments.map((a) => (
                  <StagedAttachmentChip
                    key={a.id}
                    att={a}
                    isExisting
                    onOpen={() => openTicketAttachment(basePath, ticket.id, a)}
                    onDownload={() => downloadTicketAttachment(basePath, ticket.id, a)}
                    onRemove={() => removeExistingAttachment(a)}
                  />
                ))}
              </div>
            )}

            {/* Staged (new) */}
            {stagedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="staged-attachments">
                {stagedFiles.map((s, i) => (
                  <StagedAttachmentChip
                    key={i}
                    staged={s}
                    onRemove={() => removeStaged(i)}
                  />
                ))}
              </div>
            )}

            <label className="inline-flex items-center gap-1.5 text-xs text-norea-textMuted hover:text-norea-primary cursor-pointer" data-testid="btn-attach-file">
              <Paperclip className="w-3.5 h-3.5" />
              Joindre une capture / un fichier
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.txt,.log,.json,.doc,.docx"
                className="hidden"
                onChange={(e) => { handleAddFiles(e.target.files); e.target.value = ""; }}
              />
            </label>
            <div className="text-[10px] text-norea-textMuted italic mt-0.5">
              Images, PDF, ou documents — max 10 Mo / fichier, {MAX_ATTACHMENTS} max.
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-norea-bg border-t border-norea-border flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="btn-secondary">Annuler</button>
          <button onClick={submit} disabled={busy || !submittable} className="btn-primary" data-testid={isEdit ? "btn-submit-edit" : "btn-submit-create"}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? <CheckCircle2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {isEdit ? "Enregistrer" : tab === "voice" ? "Créer + transcrire" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StagedAttachmentChip({ staged, att, isExisting, onRemove, onOpen, onDownload }) {
  // Either `staged` (newly added File) or `att` (already stored on backend)
  const file = staged?.file;
  const previewUrl = staged?.previewUrl;
  const filename = file?.name || att?.filename;
  const size = file?.size || att?.size || 0;
  const contentType = file?.type || att?.content_type || "";
  const isImg = contentType.startsWith("image/");
  const sizeLabel = size > 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(size / 1024)} Ko`;

  return (
    <div className="inline-flex items-center gap-2 bg-white border border-norea-border rounded px-2 py-1.5 text-xs" data-testid={isExisting ? `existing-att-${att.id}` : "staged-att"}>
      {isImg && previewUrl ? (
        <img src={previewUrl} alt={filename} className="w-7 h-7 rounded object-cover" />
      ) : isImg ? (
        <ImageIcon className="w-3.5 h-3.5 text-norea-primary" />
      ) : (
        <FileText className="w-3.5 h-3.5 text-norea-primary" />
      )}
      <div className={`flex flex-col min-w-0 max-w-[140px] ${onOpen ? "cursor-pointer" : ""}`} onClick={onOpen} title={onOpen ? "Ouvrir" : undefined} data-testid={isExisting ? `existing-att-open-${att.id}` : undefined}>
        <span className={`truncate text-norea-text ${onOpen ? "hover:text-norea-primary hover:underline" : ""}`}>{filename}</span>
        <span className="text-[9px] text-norea-textMuted">{sizeLabel}{isExisting ? " · existant" : ""}</span>
      </div>
      {onDownload && (
        <button type="button" onClick={onDownload} className="text-norea-textMuted hover:text-norea-primary" title="Télécharger" data-testid={`existing-att-download-${att.id}`}>
          <Download className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        onClick={onRemove}
        className="text-norea-textMuted hover:text-status-erreurText"
        title="Retirer"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ============================================================
// Check-list editor (in-modal) — ticking is open to all, editing to author/admin
// ============================================================
function ChecklistEditor({ ticket, basePath = "/tickets" }) {
  const [items, setItems] = useState(ticket.checklist || []);
  const [newText, setNewText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const dragIdRef = useRef(null);

  const doneCount = items.filter((c) => c.done).length;
  const total = items.length;
  const allDone = total > 0 && doneCount === total;

  const addItem = async () => {
    const t = newText.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/${ticket.id}/checklist`, { text: t });
      setItems(data.checklist);
      setNewText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const toggleItem = async (item) => {
    // Optimistic
    setItems((prev) => prev.map((c) => (c.id === item.id ? { ...c, done: !c.done } : c)));
    try {
      const { data } = await api.patch(`${basePath}/${ticket.id}/checklist/${item.id}`, { done: !item.done });
      setItems(data.checklist);
    } catch (e) {
      setItems((prev) => prev.map((c) => (c.id === item.id ? { ...c, done: item.done } : c)));
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const saveEdit = async (id) => {
    const t = editingText.trim();
    if (!t) { setEditingId(null); return; }
    try {
      const { data } = await api.patch(`${basePath}/${ticket.id}/checklist/${id}`, { text: t });
      setItems(data.checklist);
      setEditingId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Supprimer cet item ?")) return;
    try {
      const { data } = await api.delete(`${basePath}/${ticket.id}/checklist/${id}`);
      setItems(data.checklist);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const onDragStart = (e, id) => {
    dragIdRef.current = id;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = dragIdRef.current;
    dragIdRef.current = null;
    if (!sourceId || sourceId === targetId) return;
    const order = items.map((c) => c.id);
    const fromIdx = order.indexOf(sourceId);
    const toIdx = order.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    order.splice(toIdx, 0, order.splice(fromIdx, 1)[0]);
    // Optimistic local reorder
    const byId = Object.fromEntries(items.map((c) => [c.id, c]));
    setItems(order.map((id) => byId[id]));
    try {
      const { data } = await api.post(`${basePath}/${ticket.id}/checklist/reorder`, { order });
      setItems(data.checklist);
    } catch (err) {
      toast.error("Réordonnancement échoué");
    }
  };

  return (
    <div data-testid="checklist-editor">
      <div className="flex items-center justify-between mb-1.5">
        <label className="overline flex items-center gap-1.5">
          <ListChecks className="w-3 h-3" />
          Check-list
        </label>
        <span
          className={`text-[11px] font-mono tabular-nums px-2 py-0.5 rounded ${
            allDone ? "bg-status-completBg text-status-completText border border-status-completBorder" :
            total === 0 ? "text-norea-textMuted" :
            "bg-norea-bg text-norea-text border border-norea-border"
          }`}
          data-testid="checklist-count"
        >
          {doneCount}/{total}
          {allDone && " ✓"}
        </span>
      </div>

      {items.length > 0 && (
        <ul className="space-y-1 mb-2" data-testid="checklist-items">
          {items.map((item) => (
            <li
              key={item.id}
              draggable={editingId !== item.id}
              onDragStart={(e) => onDragStart(e, item.id)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, item.id)}
              className="group flex items-center gap-2 px-2 py-1.5 rounded hover:bg-norea-bg transition"
              data-testid={`checklist-item-${item.id}`}
            >
              <GripVertical className="w-3 h-3 text-norea-textDisabled opacity-0 group-hover:opacity-100 cursor-move flex-shrink-0" />
              <input
                type="checkbox"
                checked={!!item.done}
                onChange={() => toggleItem(item)}
                className="w-4 h-4 accent-norea-primary cursor-pointer flex-shrink-0"
                data-testid={`checklist-toggle-${item.id}`}
              />
              {editingId === item.id ? (
                <input
                  type="text"
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(item.id); if (e.key === "Escape") setEditingId(null); }}
                  onBlur={() => saveEdit(item.id)}
                  autoFocus
                  className="flex-1 bg-white border border-norea-primary/40 rounded px-2 py-0.5 text-sm focus:outline-none"
                  data-testid={`checklist-edit-${item.id}`}
                />
              ) : (
                <span
                  onDoubleClick={() => { setEditingId(item.id); setEditingText(item.text); }}
                  className={`flex-1 text-sm select-text cursor-text ${item.done ? "line-through text-norea-textMuted" : "text-norea-text"}`}
                >
                  {item.text}
                </span>
              )}
              <button
                onClick={() => deleteItem(item.id)}
                className="text-norea-textMuted hover:text-status-erreurText opacity-0 group-hover:opacity-100 transition flex-shrink-0"
                title="Supprimer"
                data-testid={`checklist-delete-${item.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          placeholder="Ajouter un élément à vérifier…"
          className="input-field text-sm flex-1"
          maxLength={300}
          data-testid="checklist-new-input"
        />
        <button
          onClick={addItem}
          disabled={busy || !newText.trim()}
          className="btn-secondary text-xs"
          data-testid="checklist-add-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Ajouter
        </button>
      </div>
      {items.length > 0 && (
        <div className="text-[10px] text-norea-textMuted italic mt-1">
          Double-clic pour renommer · glisser-déposer pour réordonner.
        </div>
      )}
    </div>
  );
}

// ============================================================
// Columns Manager modal — add / rename / recolor / delete columns
// ============================================================
function ColumnsManager({ basePath, columns: initialColumns, counts, onClose, onSaved }) {
  const [cols, setCols] = useState(initialColumns);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("slate");
  // For pending deletions: { id, target }
  const [pendingDelete, setPendingDelete] = useState(null);

  const updateCol = (id, patch) => {
    setCols((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const savePatch = async (id, patch) => {
    try {
      const { data } = await api.patch(`${basePath}/_meta/columns/${id}`, patch);
      setCols(data.columns);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const addCol = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/_meta/columns`, { label, color: newColor });
      setCols(data.columns);
      setNewLabel("");
      setNewColor("slate");
      setAdding(false);
      toast.success(`Colonne "${label}" créée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`${basePath}/_meta/columns/${pendingDelete.id}`, {
        params: pendingDelete.target ? { move_to: pendingDelete.target } : {},
      });
      setCols(data.columns);
      setPendingDelete(null);
      toast.success("Colonne supprimée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Réinitialiser aux colonnes par défaut ? Les tickets seront redistribués.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/_meta/columns/reset`);
      setCols(data.columns);
      toast.success("Colonnes réinitialisées");
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-norea-text/40 backdrop-blur-sm" onClick={onClose} data-testid="columns-modal">
      <div className="bg-white rounded-lg shadow-2xl border border-norea-border max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-norea-border flex items-center gap-3">
          <Settings2 className="w-5 h-5 text-norea-primary" />
          <h3 className="font-heading text-lg font-medium text-norea-text flex-1">Personnaliser les colonnes</h3>
          <button onClick={onClose} className="text-norea-textMuted hover:text-norea-text"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-6 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
          {cols.map((c) => (
            <ColumnRow
              key={c.id}
              column={c}
              count={counts[c.id] || 0}
              others={cols.filter((o) => o.id !== c.id)}
              onPatch={(patch) => savePatch(c.id, patch)}
              onAskDelete={(target) => setPendingDelete({ id: c.id, target, label: c.label, count: counts[c.id] || 0 })}
              localUpdate={(patch) => updateCol(c.id, patch)}
            />
          ))}

          {adding ? (
            <div className="flex items-center gap-2 p-3 border border-dashed border-norea-primary/30 bg-norea-primary/5 rounded-md" data-testid="add-column-form">
              <input
                type="text"
                placeholder="Nom de la nouvelle colonne"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                autoFocus
                className="input-field text-sm flex-1"
                onKeyDown={(e) => { if (e.key === "Enter") addCol(); }}
                data-testid="new-column-label"
              />
              <ColorPicker value={newColor} onChange={setNewColor} />
              <button onClick={addCol} disabled={busy || !newLabel.trim()} className="btn-primary text-xs" data-testid="confirm-add-column">
                <Check className="w-3.5 h-3.5" /> Ajouter
              </button>
              <button onClick={() => { setAdding(false); setNewLabel(""); }} className="btn-ghost text-xs">
                Annuler
              </button>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} disabled={cols.length >= 12} className="w-full p-3 border border-dashed border-norea-border hover:border-norea-primary/40 hover:bg-norea-bg rounded-md text-xs text-norea-textMuted hover:text-norea-primary transition flex items-center justify-center gap-1" data-testid="add-column-btn">
              <Plus className="w-3.5 h-3.5" />
              Nouvelle colonne {cols.length >= 12 && "(maximum 12)"}
            </button>
          )}
        </div>

        <div className="px-6 py-3 border-t border-norea-border bg-norea-bg flex items-center justify-between">
          <button onClick={resetDefaults} disabled={busy} className="btn-ghost text-xs text-norea-textMuted">
            Réinitialiser par défaut
          </button>
          <button onClick={() => { onSaved(); }} className="btn-primary text-sm">
            Fermer
          </button>
        </div>
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-norea-text/50" onClick={() => setPendingDelete(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-heading text-base font-medium text-norea-text mb-2">Supprimer la colonne « {pendingDelete.label} » ?</h4>
            {pendingDelete.count > 0 ? (
              <>
                <p className="text-xs text-norea-textMuted mb-3">
                  Cette colonne contient <strong>{pendingDelete.count} ticket(s)</strong>. Où veux-tu les déplacer ?
                </p>
                <select
                  value={pendingDelete.target || ""}
                  onChange={(e) => setPendingDelete({ ...pendingDelete, target: e.target.value })}
                  className="input-field text-sm w-full mb-3"
                  data-testid="delete-column-target"
                >
                  <option value="">— Choisir une colonne —</option>
                  {cols.filter((c) => c.id !== pendingDelete.id).map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </>
            ) : (
              <p className="text-xs text-norea-textMuted mb-3">Cette colonne est vide, la suppression est sûre.</p>
            )}
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setPendingDelete(null)} className="btn-secondary text-sm">Annuler</button>
              <button
                onClick={confirmDelete}
                disabled={busy || (pendingDelete.count > 0 && !pendingDelete.target)}
                className="btn-primary text-sm bg-status-erreurText hover:bg-red-700"
                data-testid="confirm-delete-column"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ColumnRow({ column, count, onPatch, onAskDelete, localUpdate }) {
  const [editing, setEditing] = useState(false);
  const [tempLabel, setTempLabel] = useState(column.label);
  const cls = colClasses(column);
  const saveLabel = () => {
    const v = tempLabel.trim();
    if (v && v !== column.label) onPatch({ label: v });
    setEditing(false);
  };
  return (
    <div className={`flex items-center gap-2 p-3 rounded-md border ${cls.border} ${cls.bg}`} data-testid={`column-row-${column.id}`}>
      <span className={`w-3 h-3 rounded-full ${cls.swatch} shrink-0`} />
      {editing ? (
        <input
          type="text"
          value={tempLabel}
          onChange={(e) => setTempLabel(e.target.value)}
          onBlur={saveLabel}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveLabel();
            if (e.key === "Escape") { setTempLabel(column.label); setEditing(false); }
          }}
          autoFocus
          maxLength={40}
          className="input-field text-sm flex-1"
          data-testid={`column-label-input-${column.id}`}
        />
      ) : (
        <button onDoubleClick={() => setEditing(true)} className="flex-1 text-left font-medium text-norea-text hover:text-norea-primary transition" title="Double-clic pour renommer" data-testid={`column-label-${column.id}`}>
          {column.label}
        </button>
      )}
      <span className="text-xs tabular-nums text-norea-textMuted bg-white border border-norea-border rounded-full px-2 py-0.5 shrink-0">
        {count}
      </span>
      <ColorPicker value={column.color} onChange={(c) => { localUpdate({ color: c }); onPatch({ color: c }); }} />
      <button onClick={() => setEditing(true)} className="p-1 text-norea-textMuted hover:text-norea-primary transition" title="Renommer" data-testid={`column-rename-${column.id}`}>
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => onAskDelete(null)} className="p-1 text-norea-textMuted hover:text-status-erreurText transition" title="Supprimer" data-testid={`column-delete-${column.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ColorPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const swatch = (COLOR_PRESETS[value] || COLOR_PRESETS.slate).swatch;
  return (
    <div className="relative">
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }} className={`w-6 h-6 rounded-full ${swatch} border-2 border-white ring-1 ring-norea-border hover:ring-norea-primary transition`} title="Changer la couleur" data-testid="color-picker-btn" />
      {open && (
        <div className="absolute right-0 top-7 z-10 bg-white border border-norea-border rounded-md shadow-lg p-2 grid grid-cols-3 gap-1" onClick={(e) => e.stopPropagation()}>
          {COLOR_OPTIONS.map((c) => (
            <button
              key={c}
              onClick={() => { onChange(c); setOpen(false); }}
              className={`w-6 h-6 rounded-full ${COLOR_PRESETS[c].swatch} hover:scale-110 transition ${c === value ? "ring-2 ring-norea-primary" : ""}`}
              data-testid={`color-${c}`}
              title={c}
            />
          ))}
        </div>
      )}
    </div>
  );
}


